import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  addLookupValue,
  updateLookupValue,
  getLookupCollection,
  getExclusions,
  setExclusions,
} from '../../services/firebase';
import type { LookupValue } from '../../types';
import toast from 'react-hot-toast';

interface CustomValueActionsProps {
  customValue: string;
  lookupCollection: string;
  // Called when an action replaces the custom value with a normalized lookup value
  onValueAdopted?: (newValue: string) => void;
  // Called after any successful action so the parent can reload its lookup state
  onRefresh?: () => void;
  disabled?: boolean;
}

type Mode = 'menu' | 'create' | 'synonym' | 'exclude';

const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export function CustomValueActions({
  customValue,
  lookupCollection,
  onValueAdopted,
  onRefresh,
  disabled = false,
}: CustomValueActionsProps) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('menu');
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [working, setWorking] = useState(false);

  const [newId, setNewId] = useState('');
  const [newDisplay, setNewDisplay] = useState('');

  const [lookupValues, setLookupValues] = useState<LookupValue[]>([]);
  const [synonymSearch, setSynonymSearch] = useState('');

  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // When opened: auto-copy, reset state, load lookup values for synonym picker
  useEffect(() => {
    if (!open) {
      setMode('menu');
      setSynonymSearch('');
      return;
    }
    setNewId(slugify(customValue));
    setNewDisplay(customValue);

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(customValue)
        .then(() => toast.success('Copied to clipboard'))
        .catch(() => {});
    }

    let cancelled = false;
    getLookupCollection(lookupCollection)
      .then((vs) => {
        if (!cancelled) setLookupValues(vs);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, customValue, lookupCollection]);

  // Anchor popover to the button
  useEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const width = 320;
    const left = Math.min(
      Math.max(8, rect.right - width),
      window.innerWidth - width - 8,
    );
    setPosition({ top: rect.bottom + 4, left });
  }, [open, mode]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filteredLookups = useMemo(() => {
    const q = synonymSearch.trim().toLowerCase();
    if (!q) return lookupValues.slice(0, 50);
    return lookupValues
      .filter(
        (lv) =>
          lv.value.toLowerCase().includes(q) ||
          lv.displayName.toLowerCase().includes(q),
      )
      .slice(0, 50);
  }, [lookupValues, synonymSearch]);

  const handleCreate = async () => {
    const id = slugify(newId);
    const displayName = newDisplay.trim();
    if (!id) {
      toast.error('ID is required');
      return;
    }
    if (!displayName) {
      toast.error('Display name is required');
      return;
    }
    if (lookupValues.some((lv) => lv.value === id)) {
      toast.error('A lookup value with this ID already exists');
      return;
    }
    setWorking(true);
    try {
      const nextSort = lookupValues.reduce(
        (max, lv) => Math.max(max, lv.sortOrder ?? 0),
        0,
      ) + 1;
      await addLookupValue(lookupCollection, {
        value: id,
        displayName,
        isActive: true,
        sortOrder: nextSort,
      });
      toast.success('Lookup value created');
      onValueAdopted?.(id);
      onRefresh?.();
      setOpen(false);
    } catch (err) {
      console.error(err);
      toast.error('Failed to create lookup value');
    } finally {
      setWorking(false);
    }
  };

  const handleAddSynonym = async (target: LookupValue) => {
    setWorking(true);
    try {
      const existing = target.synonyms || [];
      const already = existing.some(
        (s) => s.toLowerCase() === customValue.toLowerCase(),
      );
      if (!already) {
        await updateLookupValue(lookupCollection, target.id, {
          synonyms: [...existing, customValue],
        });
        toast.success(`Added synonym to ${target.displayName}`);
      } else {
        toast(`Already a synonym of ${target.displayName}`);
      }
      onValueAdopted?.(target.value);
      onRefresh?.();
      setOpen(false);
    } catch (err) {
      console.error(err);
      toast.error('Failed to add synonym');
    } finally {
      setWorking(false);
    }
  };

  const handleExclude = async () => {
    setWorking(true);
    try {
      const current = await getExclusions(lookupCollection);
      if (current.includes(customValue)) {
        toast('Already in exclusions');
      } else {
        await setExclusions(lookupCollection, [...current, customValue]);
        toast.success('Added to exclusions');
      }
      onRefresh?.();
      setOpen(false);
    } catch (err) {
      console.error(err);
      toast.error('Failed to add to exclusions');
    } finally {
      setWorking(false);
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        disabled={disabled}
        title="Manage custom value"
        aria-label="Manage custom value"
        className="inline-flex items-center justify-center p-1 text-amber-700 hover:text-amber-900 hover:bg-amber-100 rounded disabled:opacity-50 flex-shrink-0"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.573-1.066z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      </button>

      {open && position &&
        createPortal(
          <div
            ref={popoverRef}
            className="fixed z-[100] w-80 bg-white rounded-lg shadow-xl border border-gray-200"
            style={{ top: position.top, left: position.left }}
          >
            <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between gap-2">
              <div className="text-xs text-gray-500 truncate">
                Custom:{' '}
                <span className="font-mono text-amber-700">{customValue}</span>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                aria-label="Close"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {mode === 'menu' && (
              <div className="p-2 space-y-1">
                <button
                  type="button"
                  onClick={() => setMode('create')}
                  className="w-full text-left px-3 py-2 rounded hover:bg-gray-100"
                >
                  <div className="text-sm font-medium text-gray-900">
                    Create new lookup value
                  </div>
                  <div className="text-xs text-gray-500">
                    Add this value as a new entry in the collection
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setMode('synonym')}
                  className="w-full text-left px-3 py-2 rounded hover:bg-gray-100"
                >
                  <div className="text-sm font-medium text-gray-900">
                    Add as synonym
                  </div>
                  <div className="text-xs text-gray-500">
                    Attach to an existing lookup value
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setMode('exclude')}
                  className="w-full text-left px-3 py-2 rounded hover:bg-gray-100"
                >
                  <div className="text-sm font-medium text-gray-900">
                    Add to exclusions
                  </div>
                  <div className="text-xs text-gray-500">
                    Hide this value from Algolia / search
                  </div>
                </button>
              </div>
            )}

            {mode === 'create' && (
              <div className="p-3 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    ID (value)
                  </label>
                  <input
                    type="text"
                    value={newId}
                    onChange={(e) => setNewId(e.target.value)}
                    onBlur={() => setNewId((v) => slugify(v))}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Display name
                  </label>
                  <input
                    type="text"
                    value={newDisplay}
                    onChange={(e) => setNewDisplay(e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setMode('menu')}
                    disabled={working}
                    className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleCreate}
                    disabled={working}
                    className="px-3 py-1.5 text-sm bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-50"
                  >
                    {working ? 'Saving…' : 'Create'}
                  </button>
                </div>
              </div>
            )}

            {mode === 'synonym' && (
              <div className="p-3 space-y-2">
                <input
                  type="text"
                  value={synonymSearch}
                  onChange={(e) => setSynonymSearch(e.target.value)}
                  placeholder="Search lookup values…"
                  autoFocus
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded">
                  {filteredLookups.length === 0 ? (
                    <div className="p-2 text-xs text-gray-500">No matches</div>
                  ) : (
                    filteredLookups.map((lv) => (
                      <button
                        key={lv.id}
                        type="button"
                        onClick={() => handleAddSynonym(lv)}
                        disabled={working}
                        className="w-full text-left px-3 py-1.5 hover:bg-gray-100 text-sm border-b border-gray-100 last:border-b-0 disabled:opacity-50"
                      >
                        <div className="text-gray-900">{lv.displayName}</div>
                        <div className="text-xs text-gray-500 font-mono">
                          {lv.value}
                        </div>
                      </button>
                    ))
                  )}
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setMode('menu')}
                    disabled={working}
                    className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50"
                  >
                    Back
                  </button>
                </div>
              </div>
            )}

            {mode === 'exclude' && (
              <div className="p-3 space-y-3">
                <p className="text-sm text-gray-700">
                  Add{' '}
                  <span className="font-mono text-amber-700">
                    {customValue}
                  </span>{' '}
                  to the exclusion list for this lookup type?
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setMode('menu')}
                    disabled={working}
                    className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleExclude}
                    disabled={working}
                    className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
                  >
                    {working ? 'Saving…' : 'Add to exclusions'}
                  </button>
                </div>
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
